import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "../../../axios";
import { useNavigate } from "react-router-dom";
import { Input, useToast } from "@chakra-ui/react";
import {
  Switch,
  FormControl,
  FormLabel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";

const sanitizeInput = (value) => {
  if (typeof value !== "string") return "";
  return value.replace(/[<>]/g, "").replace(/javascript:/gi, "");
};

const istToUtc = (dateStr, timeStr) => {
  const istDate = new Date(`${dateStr}T${timeStr}:00+05:30`);
  return istDate.toISOString().replace("Z", "+00:00");
};

const parseNotificationReminders = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map(Number);

const AddContest = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const initialFormState = {
    contestName: "",
    contestNameHin: "",
    description: "",
    contestImage: "",
    startTime: "",
    endTime: "",
    prizePoolCoin: "",
    duration: "",
    isActive: false,
    isOffer: false,
    isCourseRequired: false,
    isPassKeyRequired: false,
    showAnswerKey:true,
    passKey: "",
    entryCoin: "",
    entryTime: "",
    winners: "",
    totalSlot: "",
    winnerPrizes: "",
    minimumQuestionsToSolve: "",
    freeContestEarningThreshold: "",
    thresholdEntryCoin: "",
    addOnSlots: "",
    showAddOnSlots: false,
    timing: "",
    notificationReminders: "",
    forNewUserOnly:false,
    minimumSlots:"",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [timeDate, setTimeDate] = useState({
    startTimeHrs: "",
    endTimeHrs: "",
    startDate: "",
    endDate: "",
    entryTime: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showActiveWarning, setShowActiveWarning] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const cancelRef = useRef();
  const [forNewUserOnly, setForNewUserOnly] = useState(false);
const [minimumSlots, setMinimumSlots] = useState(0);

  const calculateFromPrizes = useCallback((prizesStr) => {
    const prizes = prizesStr
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    const validPrizes = prizes.filter(
      (p) => !isNaN(Number(p)) && Number(p) >= 0,
    );
    const winnersCount = validPrizes.length;
    const prizePool = validPrizes.reduce((sum, p) => sum + Number(p), 0);
    return { winners: String(winnersCount), prizePoolCoin: String(prizePool) };
  }, []);

  useEffect(() => {
    if (formData.winnerPrizes.trim()) {
      const { winners, prizePoolCoin } = calculateFromPrizes(
        formData.winnerPrizes,
      );
      setFormData((prev) => ({ ...prev, winners, prizePoolCoin }));
    } else {
      setFormData((prev) => ({ ...prev, winners: "", prizePoolCoin: "" }));
    }
  }, [formData.winnerPrizes, calculateFromPrizes]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.contestName.trim())
      newErrors.contestName = "Contest name is required";
    if (!formData.contestNameHin.trim())
      newErrors.contestNameHin = "Hindi name is required";
    if (formData.isPassKeyRequired && !formData.passKey.trim())
      newErrors.passKey = "Pass key is required";

    const numericFields = [
      { key: "duration", label: "Duration" },
      { key: "entryCoin", label: "Entry Coin" },
      { key: "totalSlot", label: "Total Slots" },
    ];

    for (const { key, label } of numericFields) {
      const val = formData[key];
      if (val === "" || val === null) {
        newErrors[key] = `${label} is required`;
      } else if (isNaN(Number(val)) || Number(val) < 0) {
        newErrors[key] = `${label} must be a valid positive number`;
      }
    }

    if (!formData.winnerPrizes.trim()) {
      newErrors.winnerPrizes = "Winners prize is required";
    } else {
      const prizes = formData.winnerPrizes
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
      const allValid = prizes.every((p) => !isNaN(Number(p)) && Number(p) >= 0);
      if (!allValid || prizes.length === 0) {
        newErrors.winnerPrizes = "All prizes must be valid positive numbers";
      }
    }

    if (formData.minimumQuestionsToSolve !== "") {
      const minSolve = Number(formData.minimumQuestionsToSolve);
      if (isNaN(minSolve) || minSolve < 0 || !Number.isInteger(minSolve)) {
        newErrors.minimumQuestionsToSolve =
          "Minimum questions to solve must be a non-negative integer";
      }
    }

    if (formData.addOnSlots !== "") {
      const addOnSlots = Number(formData.addOnSlots);
      if (isNaN(addOnSlots) || addOnSlots < 0 || !Number.isInteger(addOnSlots)) {
        newErrors.addOnSlots = "Add-on slots must be a non-negative integer";
      }
    }

    if (formData.timing !== "") {
      const timing = Number(formData.timing);
      if (isNaN(timing) || timing < 0) {
        newErrors.timing = "Timing must be a valid positive number";
      }
    }

    if (formData.notificationReminders.trim()) {
      const notificationReminders = parseNotificationReminders(
        formData.notificationReminders,
      );
      const allValid =
        notificationReminders.length > 0 &&
        notificationReminders.every(
          (reminder) =>
            !isNaN(reminder) && reminder >= 0 && Number.isInteger(reminder),
        );
      if (!allValid) {
        newErrors.notificationReminders =
          "Reminder notification must be comma-separated positive numbers";
      }
    }

    if (!timeDate.startDate) newErrors.startDate = "Start date is required";
    if (!timeDate.endDate) newErrors.endDate = "End date is required";
    if (!timeDate.startTimeHrs)
      newErrors.startTimeHrs = "Start time is required";
    if (!timeDate.endTimeHrs) newErrors.endTimeHrs = "End time is required";
    if (!timeDate.entryTime) newErrors.entryTime = "Entry time is required";

    if (
      timeDate.startDate &&
      timeDate.endDate &&
      timeDate.startTimeHrs &&
      timeDate.endTimeHrs
    ) {
      const start = new Date(
        `${timeDate.startDate}T${timeDate.startTimeHrs}:00`,
      );
      const end = new Date(`${timeDate.endDate}T${timeDate.endTimeHrs}:00`);
      if (end <= start) {
        newErrors.endDate = "End time must be after start time";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized = sanitizeInput(value);
    setFormData((prevData) => ({
      ...prevData,
      [name]: sanitized,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleChangeTime = (e) => {
    const { name, value } = e.target;
    setTimeDate((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setFormData((prevData) => ({
        ...prevData,
        contestImage: file.name,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Please fix the validation errors",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsSubmitting(true);

    const { prizePoolCoin, winners, ...restFormData } = formData;
    const normalizedAddOnSlots =
      formData.addOnSlots === "" ? 0 : Number(formData.addOnSlots);
    const normalizedTiming =
      formData.timing === "" ? 0 : Number(formData.timing);
    const notificationReminders = formData.notificationReminders.trim()
      ? parseNotificationReminders(formData.notificationReminders)
      : [];
    
    let processedFormData;
    let config = {};

    if (imageFile) {
      processedFormData = new FormData();
      processedFormData.append("contestName", sanitizeInput(formData.contestName));
      processedFormData.append("contestNameHin", sanitizeInput(formData.contestNameHin));
      processedFormData.append("description", sanitizeInput(formData.description));
      processedFormData.append("startTime", istToUtc(timeDate.startDate, timeDate.startTimeHrs));
      processedFormData.append("endTime", istToUtc(timeDate.endDate, timeDate.endTimeHrs));
      processedFormData.append("entryTime", istToUtc(timeDate.startDate, timeDate.entryTime));
      processedFormData.append("duration", formData.duration);
      processedFormData.append("entryCoin", formData.entryCoin);
      processedFormData.append("totalSlot", formData.totalSlot);
      processedFormData.append("isActive", formData.isActive);
      processedFormData.append("isOffer", formData.isOffer);
      processedFormData.append("isPassKeyRequired", formData.isPassKeyRequired);
      processedFormData.append("forNewUserOnly", formData.forNewUserOnly);
      processedFormData.append("minimumSlots", formData.minimumSlots);
      processedFormData.append("passKey", sanitizeInput(formData.passKey));
      processedFormData.append("addOnSlots", normalizedAddOnSlots);
      processedFormData.append("showAddOnSlots", formData.showAddOnSlots);
      processedFormData.append("timing", normalizedTiming);
      notificationReminders.forEach((reminder) => {
        processedFormData.append("notificationReminders", reminder);
      });
      processedFormData.append("isCourseRequired", formData.isCourseRequired);
      processedFormData.append("winnerPrizes", formData.winnerPrizes
        .split(",")
        .map((prize) => prize.trim())
        .filter(Boolean));
      if (formData.minimumQuestionsToSolve !== "") {
        processedFormData.append(
          "minimumQuestionsToSolve",
          Number(formData.minimumQuestionsToSolve),
        );
      }
      if (formData.freeContestEarningThreshold !== "") {
        processedFormData.append(
          "freeContestEarningThreshold",
          Number(formData.freeContestEarningThreshold),
        );
      }
      if (formData.thresholdEntryCoin !== "") {
        processedFormData.append(
          "thresholdEntryCoin",
          formData.thresholdEntryCoin,
        );
      }
      processedFormData.append("contestImage", imageFile);
      config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
    } else {
      processedFormData = {
        ...restFormData,
        contestName: sanitizeInput(formData.contestName),
        contestNameHin: sanitizeInput(formData.contestNameHin),
        description: sanitizeInput(formData.description),
        isPassKeyRequired: formData.isPassKeyRequired,
        passKey: sanitizeInput(formData.passKey),
        addOnSlots: normalizedAddOnSlots,
        showAddOnSlots: formData.showAddOnSlots,
        timing: normalizedTiming,
        notificationReminders,
        startTime: istToUtc(timeDate.startDate, timeDate.startTimeHrs),
        endTime: istToUtc(timeDate.endDate, timeDate.endTimeHrs),
        entryTime: istToUtc(timeDate.startDate, timeDate.entryTime),
        winnerPrizes: formData.winnerPrizes
          .split(",")
          .map((prize) => prize.trim())
          .filter(Boolean),
      };
    }

    axios
      .post("addNewContest", processedFormData, config)
      .then((res) => {
        if (res.data) {
          toast({
            title: "Success! Contest Added successfully",
            status: "success",
            duration: 4000,
            isClosable: true,
            position: "top",
          });
          navigate('/dash/view-contest');
        }
      })
      .catch((err) => {
        const message = err.response?.data?.message || "Something Went Wrong!";
        toast({
          title: message,
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  

  const inputClass = (field) =>
    `mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
      errors[field]
        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
        : "border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
    }`;

  const helperText = (text) => (
    <p className="text-gray-400 text-xs mt-1 italic">{text}</p>
  );

 useEffect(() => {
  if (
    timeDate.startDate &&
    timeDate.endDate &&
    timeDate.startTimeHrs &&
    timeDate.endTimeHrs
  ) {
    try {
      const start = new Date(`${timeDate.startDate}T${timeDate.startTimeHrs}:00`);
      const end = new Date(`${timeDate.endDate}T${timeDate.endTimeHrs}:00`);

      if (!isNaN(start) && !isNaN(end) && end > start) {
        const diffMinutes = Math.floor((end - start) / 60000);

        setFormData((prev) => ({
          ...prev,
          duration: diffMinutes,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          duration: "",
        }));
      }
    } catch (err) {
      console.log("Date error", err);
    }
  }
}, [
  timeDate.startDate,
  timeDate.endDate,
  timeDate.startTimeHrs,
  timeDate.endTimeHrs,
]);
  return (
    <div className="m-6 py-8">
      <form onSubmit={handleSubmit}>
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-xl font-bold text-purple">Add Contest</h3>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Contest Name
                </label>
                <input
                  className={inputClass("contestName")}
                  name="contestName"
                  value={formData.contestName}
                  type="text"
                  required
                  maxLength={200}
                  placeholder="e.g. Weekly Quiz Challenge"
                  onChange={handleChange}
                />
                {helperText("Enter the contest title in English")}
                {errors.contestName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contestName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Contest Name (Hindi)
                </label>
                <input
                  className={inputClass("contestNameHin")}
                  name="contestNameHin"
                  value={formData.contestNameHin}
                  type="text"
                  required
                  maxLength={200}
                  placeholder="e.g. साप्ताहिक क्विज़ चुनौती"
                  onChange={handleChange}
                />
                {helperText("Enter the contest title in Hindi")}
                {errors.contestNameHin && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contestNameHin}
                  </p>
                )}
              </div>

              <div className="col-span-2">
                <label className="block text-sm text-start font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  className={inputClass("description")}
                  name="description"
                  value={formData.description}
                  rows={3}
                  maxLength={2000}
                  placeholder="e.g. Answer 10 quiz questions within 5 minutes to win exciting coin prizes!"
                  onChange={handleChange}
                />
                {helperText(
                  "Short description about the contest rules or theme",
                )}
                <p className="text-gray-400 text-xs mt-1 text-right">
                  {formData.description.length}/2000
                </p>
              </div>

              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Contest Image
                </label>
                <input
                  className={inputClass("contestImage")}
                  name="contestImage"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {helperText("Upload an image or enter URL below")}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Contest Image URL
                </label>
                <input
                  className={inputClass("contestImage")}
                  name="contestImage"
                  value={formData.contestImage}
                  type="text"
                  placeholder="e.g. https://example.com/image.jpg"
                  onChange={handleChange}
                />
                {helperText("Or enter the URL directly")}
              </div>

              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Start Date
                </label>
                <input
                  className={inputClass("startDate")}
                  name="startDate"
                  value={timeDate.startDate}
                  type="date"
                  required
                  onChange={handleChangeTime}
                />
                {helperText("Date when the contest begins")}
                {errors.startDate && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.startDate}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  End Date
                </label>
                <input
                  className={inputClass("endDate")}
                  name="endDate"
                  id="endDate"
                  value={timeDate.endDate}
                  type="date"
                  required
                  onChange={handleChangeTime}
                />
                {helperText("Date when the contest ends")}
                {errors.endDate && (
                  <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Start Time
                </label>
                <input
                  className={inputClass("startTimeHrs")}
                  name="startTimeHrs"
                  id="startTimeHrs"
                  value={timeDate.startTimeHrs}
                  type="time"
                  required
                  onChange={handleChangeTime}
                />
                {helperText("e.g. 10:00 AM")}
                {errors.startTimeHrs && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.startTimeHrs}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  End Time
                </label>
                <input
                  className={inputClass("endTimeHrs")}
                  name="endTimeHrs"
                  id="endTimeHrs"
                  value={timeDate.endTimeHrs}
                  type="time"
                  required
                  onChange={handleChangeTime}
                />
                {helperText("e.g. 11:00 PM")}
                {errors.endTimeHrs && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.endTimeHrs}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Entry Time
                </label>
                <input
                  className={inputClass("entryTime")}
                  name="entryTime"
                  id="entryTime"
                  value={timeDate.entryTime}
                  type="time"
                  required
                  onChange={handleChangeTime}
                />
                {helperText("Deadline for users to join (e.g. 09:30)")}
                {errors.entryTime && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.entryTime}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Prize Pool (Coins)
                </label>
                <input
                  className="mt-1 block w-full rounded-md shadow-sm sm:text-sm border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                  name="prizePoolCoin"
                  id="prizePoolCoin"
                  value={formData.prizePoolCoin}
                  type="number"
                  disabled
                  placeholder="Auto-calculated from Winners Prize"
                />
                {helperText("Auto-calculated: sum of all winner prizes")}
              </div>
       <div>
  <label className="block text-sm text-start font-medium text-gray-700">
    Duration (Minutes)
  </label>

  <input
    className="mt-1 block w-full rounded-md shadow-sm sm:text-sm border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
    name="duration"
    value={formData.duration}   // ✅ ONLY THIS
    type="text"
    disabled
    placeholder="Auto calculated from start & end time"
  />

  {formData.duration && (
    <p className="text-xs text-green-600 mt-1">
      ⏱ {formData.duration} minutes
    </p>
  )}

  {helperText("Auto calculated from start & end time")}

  {errors.duration && (
    <p className="text-red-500 text-xs mt-1">
      {errors.duration}
    </p>
  )}
</div>
 <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Timing
                </label>
                <input
                  className={inputClass("timing")}
                  name="timing"
                  id="timing"
                  value={formData.timing}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 5"
                  onChange={handleNumberChange}
                />
                {helperText("Timing value for contest playing")}
                {errors.timing && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.timing}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Entry Coin
                </label>
                <input
                  className={inputClass("entryCoin")}
                  name="entryCoin"
                  id="entryCoin"
                  value={formData.entryCoin}
                  type="number"
                  min="0"
                  required
                  placeholder="e.g. 500"
                  onChange={handleNumberChange}
                />
                {helperText("Coins required to join (e.g. 100, 500, 1000)")}
                {errors.entryCoin && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.entryCoin}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Free Contest Earning Threshold (Coins)
                </label>
                <input
                  className={inputClass("freeContestEarningThreshold")}
                  name="freeContestEarningThreshold"
                  id="freeContestEarningThreshold"
                  value={formData.freeContestEarningThreshold}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 200"
                  onChange={handleNumberChange}
                />
                {helperText(
                  "Users at or below this lifetime earning threshold pay Entry Coin; users above pay Threshold Entry Coin.",
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Threshold Entry Coin (Coins)
                </label>
                <input
                  className={inputClass("thresholdEntryCoin")}
                  name="thresholdEntryCoin"
                  id="thresholdEntryCoin"
                  value={formData.thresholdEntryCoin}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 500"
                  onChange={handleNumberChange}
                />
                {helperText(
                  "Entry fee charged to users who crossed the earning threshold. Defaults to Entry Coin if not set.",
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Number of Winners
                </label>
                <input
                  className="mt-1 block w-full rounded-md shadow-sm sm:text-sm border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                  name="winners"
                  id="winners"
                  value={formData.winners}
                  type="number"
                  disabled
                  placeholder="Auto-calculated from Winners Prize"
                />
                {helperText("Auto-calculated: count of comma-separated prizes")}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Total Slots
                </label>
                <input
                  className={inputClass("totalSlot")}
                  name="totalSlot"
                  id="totalSlot"
                  value={formData.totalSlot}
                  type="number"
                  min="0"
                  step="1"
                  required
                  placeholder="e.g. 100"
                  onChange={handleNumberChange}
                />
                {helperText("Max participants allowed (e.g. 50, 100, 500)")}
                {errors.totalSlot && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.totalSlot}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Add-on Slots
                </label>
                <input
                  className={inputClass("addOnSlots")}
                  name="addOnSlots"
                  id="addOnSlots"
                  value={formData.addOnSlots}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 20"
                  onChange={handleNumberChange}
                />
                {helperText("Extra slots that can be shown or opened later")}
                {errors.addOnSlots && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.addOnSlots}
                  </p>
                )}
              </div>
             
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Reminder Notification
                </label>
                <input
                  className={inputClass("notificationReminders")}
                  name="notificationReminders"
                  id="notificationReminders"
                  value={formData.notificationReminders}
                  type="text"
                  placeholder="e.g. 60,10"
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d,\s]/g, "");
                    setFormData((prev) => ({
                      ...prev,
                      notificationReminders: val,
                    }));
                    if (errors.notificationReminders) {
                      setErrors((prev) => ({
                        ...prev,
                        notificationReminders: undefined,
                      }));
                    }
                  }}
                />
                {helperText("Minutes before contest start. Example: 60,10")}
                {errors.notificationReminders && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.notificationReminders}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-start font-medium text-gray-700">
                  Minimum Questions to Solve
                </label>
                <input
                  className={inputClass("minimumQuestionsToSolve")}
                  name="minimumQuestionsToSolve"
                  id="minimumQuestionsToSolve"
                  value={formData.minimumQuestionsToSolve}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 3 (leave 0 for no minimum)"
                  onChange={handleNumberChange}
                />
                {helperText(
                  "Users who solve fewer than this many questions correctly will not be eligible for prizes. Leave 0 to allow everyone.",
                )}
                {errors.minimumQuestionsToSolve && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.minimumQuestionsToSolve}
                  </p>
                )}
              </div>

              <div className="col-span-2">
                <label className="block text-sm text-start font-medium text-gray-700">
                  Winners Prize (Comma Separated Coins)
                </label>
                <input
                  className={inputClass("winnerPrizes")}
                  name="winnerPrizes"
                  id="winnerPrizes"
                  value={formData.winnerPrizes}
                  type="text"
                  required
                  placeholder="e.g. 5000, 3000, 2000"
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d,.\s]/g, "");
                    setFormData((prev) => ({ ...prev, winnerPrizes: val }));
                    if (errors.winnerPrizes) {
                      setErrors((prev) => ({
                        ...prev,
                        winnerPrizes: undefined,
                      }));
                    }
                  }}
                />
                {helperText(
                  "Prize for each rank in order. E.g. for 3 winners: 5000, 3000, 2000 (1st, 2nd, 3rd)",
                )}
                {errors.winnerPrizes && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.winnerPrizes}
                  </p>
                )}
              </div>
              <div className="flex gap-10 mt-4">
                <FormControl display="flex" alignItems="center">
                  <Switch
                    id="isActive"
                    name="isActive"
                    isChecked={formData.isActive}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setShowActiveWarning(true);
                      } else {
                        setFormData({ ...formData, isActive: false });
                      }
                    }}
                    mr={2}
                  />
                  <FormLabel htmlFor="isActive" mb="0">
                    Contest Active
                  </FormLabel>
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <Switch
                    id="isCourseRequired"
                    name="isCourseRequired"
                    isChecked={formData.isCourseRequired}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isCourseRequired: e.target.checked,
                      })
                    }
                    mr={2}
                  />
                  <FormLabel htmlFor="isCourseRequired" mb="0">
                    Course Required
                  </FormLabel>
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <Switch
                    id="isOffer"
                    name="isOffer"
                    isChecked={formData.isOffer}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isOffer: e.target.checked,
                      })
                    }
                    mr={2}
                  />
                  <FormLabel htmlFor="isOffer" mb="0">
                    Offer Contest
                  </FormLabel>
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <Switch
                    id="isPassKeyRequired"
                    name="isPassKeyRequired"
                    isChecked={formData.isPassKeyRequired}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isPassKeyRequired: e.target.checked,
                        passKey: e.target.checked ? formData.passKey : "",
                      })
                    }
                    mr={2}
                  />
                  <FormLabel htmlFor="isPassKeyRequired" mb="0">
                    Pass Key Required
                  </FormLabel>
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <Switch
                    id="showAddOnSlots"
                    name="showAddOnSlots"
                    isChecked={formData.showAddOnSlots}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        showAddOnSlots: e.target.checked,
                      })
                    }
                    mr={2}
                  />
                  <FormLabel htmlFor="showAddOnSlots" mb="0">
                    Show Add-on Slots
                  </FormLabel>
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <Switch
                    id="showAnswerKey"
                    name="showAnswerKey"
                    isChecked={formData.showAnswerKey}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        showAnswerKey: e.target.checked,
                      })
                    }
                    mr={2}
                  />
                  <FormLabel htmlFor="showAnswerKey" mb="0">
                   Show AnswerKey
                  </FormLabel>
                </FormControl>
                {/* For New User Only */}
<div className="col-span-12 md:col-span-6">
  <FormLabel>For New Users Only</FormLabel>
  <Switch
    colorScheme="purple"
    isChecked={formData.forNewUserOnly}
    onChange={(e) =>
      setFormData({
        ...formData,
        forNewUserOnly: e.target.checked,
      })
    }
  />
</div>

{/* Minimum Slots */}
<div className="col-span-12 md:col-span-6">
  <FormLabel>Enable Minimum Slots</FormLabel>
  <Switch
    colorScheme="purple"
    isChecked={formData.minimumSlots > 0}
    onChange={(e) =>
      setFormData({
        ...formData,
        minimumSlots: e.target.checked ? 1 : 0,
      })
    }
  />
</div>

{/* Minimum Slots Input */}
{formData.minimumSlots > 0 && (
  <div className="col-span-12 md:col-span-6">
    <FormLabel>Minimum Slots</FormLabel>
    <Input
      type="number"
      min={1}
      value={formData.minimumSlots}
      onChange={(e) =>
        setFormData({
          ...formData,
          minimumSlots: Number(e.target.value),
        })
      }
      placeholder="Enter minimum slots"
    />
  </div>
)}
              </div>

              {formData.isPassKeyRequired && (
                <div className="col-span-2">
                  <label className="block text-sm text-start font-medium text-gray-700">
                    Pass Key
                  </label>
                  <input
                    className={inputClass("passKey")}
                    name="passKey"
                    id="passKey"
                    value={formData.passKey}
                    type="text"
                    placeholder="e.g. L2E2026"
                    onChange={handleChange}
                  />
                  {helperText("Users need this pass key only when pass key is enabled")}
                  {errors.passKey && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.passKey}
                    </p>
                  )}
                </div>
              )}
              
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple disabled:opacity-50"
              >
                {isSubmitting ? "Submitting..." : "Submit Contest"}
              </button>
            </div>
          </div>
        </div>
      </form>

      <AlertDialog
        isOpen={showActiveWarning}
        leastDestructiveRef={cancelRef}
        onClose={() => setShowActiveWarning(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cannot Activate Contest
            </AlertDialogHeader>
            <AlertDialogBody>
              <p className="text-red-600 font-semibold mb-2">
                A contest cannot be made active without questions.
              </p>
              <p className="text-gray-600">Please follow these steps:</p>
              <ol className="list-decimal list-inside text-gray-600 mt-2 space-y-1">
                <li>First, create and save the contest.</li>
                <li>
                  Then add questions to the contest from the contest list.
                </li>
                <li>
                  After adding questions, edit the contest to make it active.
                </li>
              </ol>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setShowActiveWarning(false)}
              >
                Got It
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </div>
  );
};

export default AddContest;
